<template>
    <div class="user-details-card">
        <!-- Header con Avatar, Usuario y Empresa -->
        <div class="user-header">
            <div class="header-row">
                <!-- Usuario -->
                <div class="header-user">
                    <div class="avatar-section">
                        <soft-avatar :img="userdata.foto_persona || IMGDEFAULT" size="lg" border-radius="lg"
                            class="user-avatar shadow-sm" :alt="userdata.primer_nombre || 'Usuario'" />
                        <div v-if="userdata.estado_persona" class="status-indicator"
                            :class="userdata.estado_persona === 'activo' ? 'status-active' : 'status-inactive'"></div>
                    </div>
                    <div class="user-info">
                        <h4 class="user-name">
                            {{ fullName(userdata) || message }}
                            <i v-if="userdata.genero == '1'" class="fas fa-mars gender-icon ms-2"></i>
                            <i v-else-if="userdata.genero == '2'" class="fas fa-venus gender-icon ms-2"></i>
                            <i v-else-if="userdata.genero == '3'" class="fas fa-transgender gender-icon ms-2"></i>
                        </h4>
                        <p class="user-contact">
                            <i class="fas fa-phone me-2" style="opacity: 0.7;"></i>
                            {{ userdata.celular_movil || message }}
                        </p>
                        <div class="user-badges">
                            <span v-if="userdata.estado_persona" class="badge"
                                :class="userdata.estado_persona === 'activo' ? 'badge-active' : 'badge-inactive'">
                                <i :class="userdata.estado_persona === 'activo' ? 'fas fa-check-circle' : 'fas fa-times-circle'"
                                    class="me-1"></i>
                                {{ userdata.estado_persona === 'activo' ? 'Activo' : 'Inactivo' }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Separador -->
                <div class="header-divider"></div>

                <!-- Empresa -->
                <div class="header-empresa">
                    <template v-if="empresa">
                        <img v-if="empresa.logo" :src="empresa.logo" :alt="empresa.nombre"
                            class="empresa-logo" />
                        <div class="empresa-icon" v-else>
                            <i class="fas fa-building"></i>
                        </div>
                        <div class="empresa-info">
                            <h6 class="empresa-name">{{ empresa.nombre }}</h6>
                            <p class="empresa-detail" v-if="empresa.nit">
                                NIT: {{ empresa.nit }}<span v-if="empresa.dv">-{{ empresa.dv }}</span>
                            </p>
                            <p class="empresa-detail" v-if="empresa.email">
                                <i class="fas fa-envelope me-1" style="font-size: 10px;"></i>
                                {{ empresa.email }}
                            </p>
                            <p class="empresa-detail" v-if="empresa.telefono || empresa.celular">
                                <i class="fas fa-phone me-1" style="font-size: 10px;"></i>
                                {{ empresa.telefono || empresa.celular }}
                            </p>
                        </div>
                    </template>
                    <template v-else>
                        <div class="empresa-icon" style="opacity: 0.4;">
                            <i class="fas fa-building"></i>
                        </div>
                        <div class="empresa-info">
                            <span class="empresa-detail" style="opacity: 0.6;">Sin empresa asociada</span>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- Información Detallada -->
        <div class="details-grid">
            <!-- Información Personal -->
            <div class="detail-section">
                <h6 class="section-title">
                    <i class="fas fa-user text-primary me-2"></i>
                    Información Personal
                </h6>

                <div class="detail-item">
                    <div class="detail-icon">
                        <i class="fas fa-id-card text-info"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Documento de Identidad</label>
                        <span class="detail-value">
                            {{ userdata.cedula_persona ? `DOC: ${userdata.cedula_persona}` : message }}
                        </span>
                    </div>
                </div>

                <div class="detail-item" v-if="userdata.email">
                    <div class="detail-icon">
                        <i class="fas fa-envelope text-warning"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Correo Electrónico</label>
                        <a :href="`mailto:${userdata.email}`" class="detail-value link-value">
                            {{ userdata.email }}
                        </a>
                    </div>
                </div>

                <div class="detail-item" v-if="userdata.telefono_fijo">
                    <div class="detail-icon">
                        <i class="fas fa-phone text-info"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Teléfono Fijo</label>
                        <a :href="`tel:${userdata.telefono_fijo}`" class="detail-value link-value">
                            {{ userdata.telefono_fijo }}
                        </a>
                    </div>
                </div>
            </div>

            <!-- Información de Contacto -->
            <div class="detail-section">
                <h6 class="section-title">
                    <i class="fas fa-address-book text-success me-2"></i>
                    Contacto
                </h6>

                <div class="detail-item" v-if="userdata.celular_movil">
                    <div class="detail-icon">
                        <i class="fas fa-mobile-alt text-success"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Teléfono Móvil</label>
                        <a :href="`tel:${userdata.celular_movil}`" class="detail-value link-value">
                            {{ userdata.celular_movil }}
                        </a>
                    </div>
                </div>

                <div class="detail-item" v-if="userdata.celular_whatsapp">
                    <div class="detail-icon">
                        <i class="fab fa-whatsapp text-success"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">WhatsApp</label>
                        <a :href="`https://wa.me/${userdata.celular_whatsapp.replace(/\D/g, '')}`"
                            class="detail-value link-value" target="_blank">
                            {{ userdata.celular_whatsapp }}
                        </a>
                    </div>
                </div>

                <div class="detail-item" v-if="userdata.direccion_persona">
                    <div class="detail-icon">
                        <i class="fas fa-map-marker-alt text-danger"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Dirección</label>
                        <span class="detail-value">{{ userdata.direccion_persona }}</span>
                    </div>
                </div>
            </div>

            <!-- Información de Ubicación -->
            <div class="detail-section" v-if="userdata.id_pais">
                <h6 class="section-title">
                    <i class="fas fa-globe text-info me-2"></i>
                    Ubicación
                </h6>

                <div class="location-card">
                    <div class="location-icon">
                        <i class="fas fa-map-marked-alt text-primary"></i>
                    </div>
                    <div class="location-details">
                        <span class="location-text">
                            {{ userdata.id_pais?.name || message }}
                            <span v-if="userdata.id_estado?.name"> • {{ userdata.id_estado.name }}</span>
                            <span v-if="userdata.id_ciudad?.name"> • {{ userdata.id_ciudad.name }}</span>
                        </span>
                        <small class="location-subtitle">País • Departamento • Ciudad</small>
                    </div>
                </div>
            </div>

            <!-- Empresa (detalles adicionales) -->
            <div class="detail-section" v-if="empresa && (empresa.direccion || empresa.pagina_web || empresa.razon_social || empresa.id_pais)">
                <h6 class="section-title">
                    <i class="fas fa-building text-danger me-2"></i>
                    Empresa — {{ empresa.nombre }}
                </h6>

                <div class="detail-item" v-if="empresa.razon_social && empresa.razon_social !== empresa.nombre">
                    <div class="detail-icon">
                        <i class="fas fa-file-alt text-secondary"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Razón Social</label>
                        <span class="detail-value">{{ empresa.razon_social }}</span>
                    </div>
                </div>

                <div class="detail-item" v-if="empresa.direccion">
                    <div class="detail-icon">
                        <i class="fas fa-map-marker-alt text-danger"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Dirección</label>
                        <span class="detail-value">{{ empresa.direccion }}</span>
                    </div>
                </div>

                <div class="detail-item" v-if="empresa.pagina_web">
                    <div class="detail-icon">
                        <i class="fas fa-globe text-info"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Página Web</label>
                        <a :href="empresa.pagina_web.startsWith('http') ? empresa.pagina_web : `https://${empresa.pagina_web}`"
                            class="detail-value link-value" target="_blank">
                            {{ empresa.pagina_web }}
                        </a>
                    </div>
                </div>

                <div v-if="empresa.id_pais || empresa.id_estado || empresa.id_ciudad" class="location-card mt-2">
                    <div class="location-icon">
                        <i class="fas fa-map-marked-alt text-primary"></i>
                    </div>
                    <div class="location-details">
                        <span class="location-text">
                            {{ empresa.id_pais?.name || '' }}
                            <span v-if="empresa.id_estado?.name"> • {{ empresa.id_estado.name }}</span>
                            <span v-if="empresa.id_ciudad?.name"> • {{ empresa.id_ciudad.name }}</span>
                        </span>
                        <small class="location-subtitle">Ubicación de la empresa</small>
                    </div>
                </div>
            </div>

            <!-- Redes Sociales -->
            <div class="detail-section"
                v-if="userdata.facebook || userdata.instagram || userdata.linkedin || userdata.tiktok">
                <h6 class="section-title">
                    <i class="fas fa-share-alt text-primary me-2"></i>
                    Redes Sociales
                </h6>

                <div class="social-links">
                    <a v-if="userdata.facebook" :href="userdata.facebook" target="_blank" class="social-link facebook">
                        <i class="fab fa-facebook-f"></i>
                        <span>Facebook</span>
                    </a>

                    <a v-if="userdata.instagram" :href="userdata.instagram" target="_blank"
                        class="social-link instagram">
                        <i class="fab fa-instagram"></i>
                        <span>Instagram</span>
                    </a>

                    <a v-if="userdata.linkedin" :href="userdata.linkedin" target="_blank" class="social-link linkedin">
                        <i class="fab fa-linkedin-in"></i>
                        <span>LinkedIn</span>
                    </a>

                    <a v-if="userdata.tiktok" :href="userdata.tiktok" target="_blank" class="social-link tiktok">
                        <i class="fab fa-tiktok"></i>
                        <span>TikTok</span>
                    </a>
                </div>
            </div>

            <!-- Información del Sistema -->
            <div class="detail-section" v-if="userdata.created_at || userdata.updated_at">
                <h6 class="section-title">
                    <i class="fas fa-cog text-secondary me-2"></i>
                    Sistema
                </h6>

                <div class="detail-item" v-if="userdata.created_at">
                    <div class="detail-icon">
                        <i class="fas fa-calendar-plus text-success"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Fecha de Registro</label>
                        <span class="detail-value">
                            {{ new Date(userdata.created_at).toLocaleDateString('es-ES', {
                                year: 'numeric', month:
                            'long', day: 'numeric' }) }}
                        </span>
                    </div>
                </div>

                <div class="detail-item" v-if="userdata.updated_at">
                    <div class="detail-icon">
                        <i class="fas fa-clock text-info"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Última Actualización</label>
                        <span class="detail-value">
                            {{ new Date(userdata.updated_at).toLocaleDateString('es-ES', {
                                year: 'numeric', month:
                            'long', day: 'numeric' }) }}
                        </span>
                    </div>
                </div>

                <div class="detail-item" v-if="userdata.slug">
                    <div class="detail-icon">
                        <i class="fas fa-link text-warning"></i>
                    </div>
                    <div class="detail-content">
                        <label class="detail-label">Slug</label>
                        <span class="detail-value">{{ userdata.slug }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Acciones Rápidas -->
        <div class="quick-actions">
            <button v-if="userdata.email" class="action-btn email-btn" @click="sendEmail" title="Enviar correo">
                <i class="fas fa-envelope"></i>
            </button>

            <button v-if="userdata.celular_whatsapp" class="action-btn whatsapp-btn" @click="openWhatsApp"
                title="Contactar por WhatsApp">
                <i class="fab fa-whatsapp"></i>
            </button>

            <button v-if="userdata.celular_movil" class="action-btn phone-btn" @click="makePhoneCall" title="Llamar">
                <i class="fas fa-phone"></i>
            </button>

            <button class="action-btn print-btn" @click="printUserDetails" title="Imprimir detalles">
                <i class="fas fa-print"></i>
            </button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.user-details-card {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 1rem;
    padding: 1.5rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    max-height: 70vh;
    overflow-y: auto;
    position: relative;
}

.user-header {
    padding: 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 0.75rem;
    margin-bottom: 1.5rem;
    color: white;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at 30% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    }

    .header-row {
        display: flex;
        align-items: center;
        gap: 1rem;
        position: relative;
        z-index: 2;
    }

    .header-user {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        flex: 1;
    }

    .header-divider {
        width: 1px;
        align-self: stretch;
        background: rgba(255, 255, 255, 0.25);
        min-height: 60px;
        flex-shrink: 0;
    }

    .header-empresa {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex: 1;
        min-width: 0;
    }

    .empresa-logo {
        width: 44px;
        height: 44px;
        object-fit: contain;
        border-radius: 0.5rem;
        border: 2px solid rgba(255, 255, 255, 0.3);
        background: white;
        flex-shrink: 0;
    }

    .empresa-icon {
        width: 44px;
        height: 44px;
        border-radius: 0.5rem;
        border: 2px solid rgba(255, 255, 255, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        background: rgba(255, 255, 255, 0.1);

        i {
            font-size: 1.1rem;
        }
    }

    .empresa-info {
        min-width: 0;
    }

    .empresa-name {
        font-size: 0.95rem;
        font-weight: 600;
        margin-bottom: 0.15rem;
        color: white;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .empresa-detail {
        margin-bottom: 0;
        font-size: 0.78rem;
        opacity: 0.85;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .avatar-section {
        position: relative;

        .user-avatar {
            border: 3px solid rgba(255, 255, 255, 0.3);
        }

        .status-indicator {
            position: absolute;
            bottom: 2px;
            right: 2px;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 2px solid white;

            &.status-active {
                background-color: #28a745;
            }

            &.status-inactive {
                background-color: #dc3545;
            }
        }
    }

    .user-info {
        flex: 1;

        .user-name {
            font-size: 1.15rem;
            font-weight: 600;
            margin-bottom: 0.35rem;
            color: white;

            .gender-icon {
                opacity: 0.8;
                font-size: 1rem;
            }
        }

        .user-contact {
            margin-bottom: 0.5rem;
            opacity: 0.9;
            font-size: 0.9rem;
        }

        .user-badges {
            .badge {
                padding: 0.35rem 0.7rem;
                border-radius: 1rem;
                font-size: 0.75rem;
                font-weight: 500;

                &.badge-active {
                    background-color: #28a745 !important;
                }

                &.badge-inactive {
                    background-color: #dc3545 !important;
                }
            }
        }
    }
}

.details-grid {
    display: grid;
    gap: 1.25rem;
}

.detail-section {
    background: white;
    border-radius: 0.75rem;
    padding: 1.25rem;
    border: 1px solid #e9ecef;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }

    .section-title {
        color: #495057;
        font-weight: 600;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid #f8f9fa;
    }
}

.detail-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f8f9fa;

    &:last-child {
        border-bottom: none;
    }

    .detail-icon {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f8f9fa;
        border-radius: 0.5rem;
        flex-shrink: 0;

        i {
            font-size: 0.9rem;
        }
    }

    .detail-content {
        flex: 1;

        .detail-label {
            display: block;
            font-size: 0.8rem;
            color: #6c757d;
            font-weight: 500;
            margin-bottom: 0.25rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .detail-value {
            font-weight: 600;
            color: #495057;

            &.link-value {
                color: #007bff;
                text-decoration: none;

                &:hover {
                    text-decoration: underline;
                }
            }
        }

        .badge {
            font-size: 0.75rem;
            padding: 0.35rem 0.65rem;
        }
    }
}

.location-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: #f8f9fa;
    border-radius: 0.5rem;
    padding: 1rem;
    border: 1px solid #e9ecef;

    .location-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
        border-radius: 0.5rem;

        i {
            font-size: 1.25rem;
        }
    }

    .location-details {
        flex: 1;

        .location-text {
            font-weight: 600;
            color: #495057;
            display: block;
            margin-bottom: 0.25rem;
        }

        .location-subtitle {
            color: #6c757d;
            font-size: 0.8rem;
        }
    }
}

.social-links {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.75rem;

    .social-link {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        border-radius: 0.5rem;
        text-decoration: none;
        color: white;
        font-weight: 500;
        transition: all 0.2s ease;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
            color: white;
            text-decoration: none;
        }

        &.facebook {
            background: linear-gradient(135deg, #1877f2, #4267B2);
        }

        &.instagram {
            background: linear-gradient(135deg, #E4405F, #833AB4, #F77737);
        }

        &.linkedin {
            background: linear-gradient(135deg, #0077B5, #005885);
        }

        &.tiktok {
            background: linear-gradient(135deg, #000000, #333333);
        }

        i {
            font-size: 1.1rem;
        }
    }
}

.quick-actions {
    position: absolute;
    top: 1rem;
    right: 1rem;
    display: flex;
    gap: 0.5rem;
    z-index: 10;

    .action-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 0.9rem;
        transition: all 0.2s ease;
        cursor: pointer;

        &:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        &.email-btn {
            background: linear-gradient(135deg, #007bff, #0056b3);
        }

        &.whatsapp-btn {
            background: linear-gradient(135deg, #25d366, #128c7e);
        }

        &.phone-btn {
            background: linear-gradient(135deg, #28a745, #1e7e34);
        }

        &.print-btn {
            background: linear-gradient(135deg, #6c757d, #495057);
        }
    }
}

// Responsive Design
@media (max-width: 768px) {
    .user-details-card {
        padding: 1rem;
        max-height: 85vh;
    }

    .user-header {
        padding: 1rem;

        .header-row {
            flex-direction: column;
            gap: 0.75rem;
        }

        .header-user {
            flex-direction: column;
            align-items: center;
            text-align: center;
        }

        .header-divider {
            width: 100%;
            height: 1px;
            min-height: unset;
            align-self: unset;
        }

        .header-empresa {
            flex-direction: column;
            align-items: center;
            text-align: center;
        }

        .empresa-info {
            text-align: center;
        }

        .empresa-name,
        .empresa-detail {
            text-align: center;
        }
    }

    .detail-item {
        flex-direction: column;
        gap: 0.5rem;

        .detail-icon {
            align-self: flex-start;
        }
    }

    .location-card {
        flex-direction: column;
        text-align: center;
    }

    .quick-actions {
        position: static;
        justify-content: center;
        margin-top: 1rem;
        padding-top: 1rem;
        border-top: 1px solid #e9ecef;
    }
}

// Scrollbar
.user-details-card {
    scrollbar-width: thin;
    scrollbar-color: #6c757d #f8f9fa;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f8f9fa;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: #6c757d;
        border-radius: 3px;

        &:hover {
            background: #495057;
        }
    }
}

// Animaciones
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.detail-section {
    animation: slideIn 0.3s ease-out;

    &:nth-child(2) {
        animation-delay: 0.1s;
    }

    &:nth-child(3) {
        animation-delay: 0.2s;
    }

    &:nth-child(4) {
        animation-delay: 0.3s;
    }

    &:nth-child(5) {
        animation-delay: 0.4s;
    }
}
</style>
<script>
import SoftAvatar from "@/components/SoftAvatar.vue";
import { IMGDEFOULT } from "../../../constans.js";
import { fullName } from "../useMembership.js";

export default {
    props: {
        userdata: {
            type: Object,
            required: true
        },
        empresa: {
            type: Object,
            default: null
        }
    },
    components: {
        SoftAvatar
    },

    setup(props) {
        const message = 'Sin informacion';
        const openWhatsApp = () => {
            if (props.userdata.celular_whatsapp) {
                // Limpiar el número de caracteres no numéricos
                const cleanNumber = props.userdata.celular_whatsapp.replace(/\D/g, '');

                // Crear mensaje personalizado (opcional)
                const message = `Hola ${props.userdata.primer_nombre || 'Usuario'}, te contacto desde la plataforma.`;

                // Construir URL de WhatsApp con mensaje
                const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;

                // Abrir en nueva ventana
                window.open(whatsappUrl, '_blank');
            } else {
                console.warn('No hay número de WhatsApp disponible');
            }
        };
        const sendEmail = () => {
            if (props.userdata.email) {
                // Crear asunto y cuerpo del email (opcional)
                const subject = `Contacto - ${props.userdata.primer_nombre || 'Usuario'}`;
                const body = `Hola ${props.userdata.primer_nombre || 'Usuario'},\n\nTe contacto desde la plataforma.\n\nSaludos.`;

                // Construir URL de mailto
                const mailtoUrl = `mailto:${props.userdata.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

                // Abrir cliente de email
                window.open(mailtoUrl, '_blank');
            } else {
                console.warn('No hay email disponible');
            }
        };
        const makePhoneCall = () => {
            if (props.userdata.celular_movil) {
                // Crear URL de tel
                const telUrl = `tel:${props.userdata.celular_movil}`;

                // Abrir aplicación de teléfono
                window.open(telUrl, '_self');
            } else {
                console.warn('No hay número de teléfono disponible');
            }
        };
        const printUserDetails = () => {
            // Crear una ventana nueva con solo el contenido del usuario para imprimir
            const printWindow = window.open('', '_blank');

            if (printWindow) {
                const userInfo = props.userdata;

                // HTML para imprimir con estilos incluidos
                const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Detalles de Usuario - ${fullName(userInfo) || 'Usuario'}</title>
        <meta charset="utf-8">
        <style>
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 20px;
            color: #333;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #007bff;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .header h1 {
            color: #007bff;
            margin: 0;
            font-size: 28px;
          }
          .header p {
            color: #666;
            margin: 5px 0;
          }
          .section {
            margin-bottom: 25px;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            padding: 15px;
            background: #f8f9fa;
          }
          .section-title {
            font-size: 18px;
            font-weight: bold;
            color: #495057;
            margin-bottom: 15px;
            border-bottom: 1px solid #dee2e6;
            padding-bottom: 8px;
          }
          .detail-row {
            display: flex;
            margin-bottom: 10px;
            border-bottom: 1px solid #e9ecef;
            padding-bottom: 8px;
          }
          .detail-row:last-child {
            border-bottom: none;
            margin-bottom: 0;
          }
          .detail-label {
            font-weight: bold;
            min-width: 150px;
            color: #6c757d;
            font-size: 14px;
          }
          .detail-value {
            flex: 1;
            color: #495057;
          }
          .badge {
            background: #007bff;
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
          }
          .badge.active {
            background: #28a745;
          }
          .badge.inactive {
            background: #dc3545;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #666;
            border-top: 1px solid #dee2e6;
            padding-top: 15px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${fullName(userInfo) || 'Usuario'}</h1>
          <p>Detalles del Usuario</p>
          ${userInfo.estado_persona ? `<span class="badge ${userInfo.estado_persona === 'activo' ? 'active' : 'inactive'}">${userInfo.estado_persona}</span>` : ''}
        </div>

        <div class="section">
          <div class="section-title">📋 Información Personal</div>
          ${userInfo.cedula_persona ? `<div class="detail-row"><div class="detail-label">Documento:</div><div class="detail-value">DOC: ${userInfo.cedula_persona}</div></div>` : ''}
          ${userInfo.genero ? `<div class="detail-row"><div class="detail-label">Género:</div><div class="detail-value">${userInfo.genero === '1' ? 'Masculino' : userInfo.genero === '2' ? 'Femenino' : 'Otro'}</div></div>` : ''}
          ${userInfo.fecha_nacimiento ? `<div class="detail-row"><div class="detail-label">Fecha de Nacimiento:</div><div class="detail-value">${new Date(userInfo.fecha_nacimiento).toLocaleDateString('es-ES')}</div></div>` : ''}
        </div>

        <div class="section">
          <div class="section-title">📞 Información de Contacto</div>
          ${userInfo.email ? `<div class="detail-row"><div class="detail-label">Email:</div><div class="detail-value">${userInfo.email}</div></div>` : ''}
          ${userInfo.celular_movil ? `<div class="detail-row"><div class="detail-label">Teléfono Móvil:</div><div class="detail-value">${userInfo.celular_movil}</div></div>` : ''}
          ${userInfo.celular_whatsapp ? `<div class="detail-row"><div class="detail-label">WhatsApp:</div><div class="detail-value">${userInfo.celular_whatsapp}</div></div>` : ''}
          ${userInfo.telefono_fijo ? `<div class="detail-row"><div class="detail-label">Teléfono Fijo:</div><div class="detail-value">${userInfo.telefono_fijo}</div></div>` : ''}
          ${userInfo.direccion_persona ? `<div class="detail-row"><div class="detail-label">Dirección:</div><div class="detail-value">${userInfo.direccion_persona}</div></div>` : ''}
        </div>

        ${userInfo.id_pais ? `
        <div class="section">
          <div class="section-title">🌍 Ubicación</div>
          <div class="detail-row">
            <div class="detail-label">Ubicación:</div>
            <div class="detail-value">
              ${userInfo.id_pais.name || ''}
              ${userInfo.id_estado?.name ? ` • ${userInfo.id_estado.name}` : ''}
              ${userInfo.id_ciudad?.name ? ` • ${userInfo.id_ciudad.name}` : ''}
            </div>
          </div>
        </div>
        ` : ''}

        ${(userInfo.facebook || userInfo.instagram || userInfo.linkedin || userInfo.tiktok) ? `
        <div class="section">
          <div class="section-title">🌐 Redes Sociales</div>
          ${userInfo.facebook ? `<div class="detail-row"><div class="detail-label">Facebook:</div><div class="detail-value">${userInfo.facebook}</div></div>` : ''}
          ${userInfo.instagram ? `<div class="detail-row"><div class="detail-label">Instagram:</div><div class="detail-value">${userInfo.instagram}</div></div>` : ''}
          ${userInfo.linkedin ? `<div class="detail-row"><div class="detail-label">LinkedIn:</div><div class="detail-value">${userInfo.linkedin}</div></div>` : ''}
          ${userInfo.tiktok ? `<div class="detail-row"><div class="detail-label">TikTok:</div><div class="detail-value">${userInfo.tiktok}</div></div>` : ''}
        </div>
        ` : ''}

        <div class="section">
          <div class="section-title">⚙️ Información del Sistema</div>
          ${userInfo.created_at ? `<div class="detail-row"><div class="detail-label">Fecha de Registro:</div><div class="detail-value">${new Date(userInfo.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</div></div>` : ''}
          ${userInfo.updated_at ? `<div class="detail-row"><div class="detail-label">Última Actualización:</div><div class="detail-value">${new Date(userInfo.updated_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</div></div>` : ''}
          ${userInfo.slug ? `<div class="detail-row"><div class="detail-label">Slug:</div><div class="detail-value">${userInfo.slug}</div></div>` : ''}
          ${userInfo.id ? `<div class="detail-row"><div class="detail-label">ID del Usuario:</div><div class="detail-value">${userInfo.id}</div></div>` : ''}
        </div>

        <div class="footer">
          <p>Documento generado el ${new Date().toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })}</p>
        </div>
      </body>
      </html>
    `;

                // Escribir contenido en la nueva ventana
                printWindow.document.write(printContent);
                printWindow.document.close();

                // Esperar a que se cargue y luego imprimir
                printWindow.onload = () => {
                    printWindow.focus();
                    printWindow.print();

                    // Cerrar ventana después de imprimir (opcional)
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                };
            } else {
                console.error('No se pudo abrir la ventana de impresión');
            }
        };

        // También puedes crear una versión simple de imprimir solo la página actual
        const simplePrint = () => {
            window.print();
        };

        return {
            fullName,
            IMGDEFOULT,
            message,
            openWhatsApp,
            sendEmail,
            makePhoneCall,
            printUserDetails,
            simplePrint
        }
    }
}
</script>
